<template>
    <el-container
        class="property-requirement-steps"
        direction="vertical"
        v-if="stepsLeft"
        v-loading="stepsLeft && loading"
    >
        <div class="property-requirement-steps__header">
            <app-i18n
                class="property-requirement-steps__header__greeting"
                tag="p"
                code="entities.property.requirementSteps.title"
                :args="[`<strong>${userName}</strong>`]"
            />
            <app-i18n
                tag="p"
                code="entities.property.requirementSteps.caption"
            />
        </div>
        <div class="property-requirement-steps__progress">
            <el-progress
                class="property-requirement-steps__progress__bar"
                :percentage="stepsPercentage"
                :show-text="false"
                color="#2acccc"
                :stroke-width="8"
            />
            <div>
                <el-tooltip
                    :content="tooltipCaption"
                    placement="bottom"
                >
                    <font-awesome-icon
                        class="property-requirement-steps__progress__icon"
                        :icon="progressBarIcon"
                        size="1x"
                    />
                </el-tooltip>
            </div>
            <app-i18n
                class="property-requirement-steps__progress__label"
                code="entities.property.requirementSteps.progressCaption"
                :args="[`<strong>${stepsLeft}</strong>`]"
            />
        </div>
        <div class="property-requirement-steps__cards">
            <el-row>
                <simplebar
                    data-simplebar
                    data-simplebar-auto-hide="false"
                >
                    <el-col
                        v-for="(step, index) in todoSteps"
                        :key="`property-requirement-steps__col--${Object.keys(steps)[index]}`"
                        class="property-requirement-steps__col"
                    >
                        <tag
                            :is="typeof step.link.urlLink === 'string' ? 'a' : 'div'"
                            :href="typeof step.link.urlLink === 'string' ? step.link.urlLink : false"
                            @click="typeof step.onClick === 'function' ? stepOnClick(step) : false"
                        >
                            <div
                                class="property-requirement-steps__card"
                                :class="{ 'property-requirement-steps__card--inactive': !step.active }"
                                :style="{ backgroundColor: stepBackgroundColor(index) }"
                                @click="step.onClick || false"
                            >
                                <span
                                    class="property-requirement-steps__card__icon"
                                    :class="step.icon"
                                />
                                <p
                                    v-text="step.title"
                                    class="property-requirement-steps__card__title"
                                />
                                <el-link class="property-requirement-steps__card__link">
                                    {{ step.link.label }}
                                    <font-awesome-icon
                                        :icon="stepLinkIcon"
                                        size="1x"
                                        class="property-requirement-steps__card__link__icon"
                                    />
                                </el-link>
                            </div>
                        </tag>
                    </el-col>
                </simplebar>
            </el-row>
        </div>
        <el-dialog :visible.sync="dialogVisible">
            <component
                v-if="dialogComponent"
                :is="dialogComponent"
                :id="dialogId"
                :navigate-on-submit="false"
            />
        </el-dialog>
    </el-container>
</template>

<script>
import { mapGetters } from 'vuex';
import { i18n } from '@shared/i18n';
import { faQuestionCircle, faChevronRight } from '@fortawesome/free-solid-svg-icons';

import PropertyFormPage from'@modules/property/components/property-form-page.vue';
import ProfileFormPage from'@modules/auth/components/profile-form-page.vue';

export default {
    name: 'PropertyRequirementSteps',
    data() {
        return {
            dialogVisible: false,
            dialogComponent: null,
            dialogId: null,
            stepLinkIcon: faChevronRight,
            progressBarIcon: faQuestionCircle,
            tooltipCaption: i18n('entities.property.requirementSteps.tooltipCaption')
        };
    },
    computed: {
        ...mapGetters({
            currentUser: 'auth/currentUser',
            currentManage: 'auth/manage/currentManage',
            steps: 'property/requirements/steps',
            loading: 'property/requirements/loading',
        }),
        stepsList() {
            return Object.values(this.steps).filter(step => step.active);
        },
        userName() {
            if(!this.currentUser || !this.currentUser.firstName) return 'usuario';
            return this.currentUser.firstName;
        },
        todoSteps() {
            return this.stepsList.filter(step => !step.done);
        },
        doneSteps() {
            return this.stepsList.filter(step => step.done);
        },
        stepsTotal() {
            return this.stepsList.length;
        },
        stepsLeft() {
            return this.stepsTotal - this.doneSteps.length;
        },
        stepsPercentage() {
            return this.doneSteps.length / this.stepsTotal * 100;
        }
    },
    methods: {
        stepBackgroundColor(index) {
            const { active } = this.stepsList[index];
            if (!active) return '#999999';
            return index % 2 === 0
                ? '#279ce2'
                : '#2acccc'
        },

        stepOnClick(step) {
            step.onClick.bind(this)();
        },
    },
    components: {
        PropertyFormPage,
        ProfileFormPage,
    },
    watch: {
        steps: {
            deep: true,
            handler() {
                this.dialogVisible = null;
                this.dialogComponent = null;
                this.dialogId = null;
            }
        }
    }
}
</script>

<style>
.property-requirement-steps {
    background-color: #ffffff;
    color: #4c4c4c;
    border-radius: 8px;
    display: grid;
    grid-template-areas:
        '. header progress .'
        'cards cards cards cards';
    grid-template-columns: 40px minmax(0, 100%) 200px 40px;
    padding: 30px 0 10px;
    margin-bottom: 20px;
}

.property-requirement-steps p {
    margin: 0;
}

.property-requirement-steps__header {
    grid-area: header;
    font-size: 18px;
    line-height: 1.11;
    color: #4c4c4c;
}

p.property-requirement-steps__header__greeting {
    margin-bottom: 10px;
    font-size: 22px;
}

.property-requirement-steps__progress {
    grid-area: progress;
    font-size: 12px;
    display: grid;
    grid-template-areas:
        'bar icon'
        'label label';
    grid-template-columns: 1fr 12px;
    grid-template-rows: 12px auto;
    grid-column-gap: 4px;
}

.property-requirement-steps__progress__icon {
    grid-area: icon;
    display: grid;
    align-items: center;
    justify-items: center;
    cursor: pointer;
}
.property-requirement-steps__progress__bar {
    grid-area: bar;
    display: grid;
    align-items: center;
}

.property-requirement-steps__progress__label {
    grid-area: label;
}

.property-requirement-steps__cards {
    grid-area: cards;
}

.property-requirement-steps__card {
    display: grid;
    grid-template-areas:
        'icon title'
        'icon link';
    grid-template-columns: 60px 1fr;
    grid-template-rows: 1fr 20px;
    white-space: normal;
    padding: 30px 25px 20px;
    height: 160px;
    box-sizing: border-box;
    border-radius: 4px;
    box-shadow: 0 2px 10px 0 rgba(0, 0, 0, .12);
    cursor: pointer;
}

.property-requirement-steps__card--inactive {
    cursor: not-allowed;
    opacity: .6;
}

.property-requirement-steps__card__icon {
    grid-area: icon;
    color: #ffffff;
    font-size: 30px;
    margin: 2px 0 0 2px;
}

.property-requirement-steps__card__title {
    grid-area: title;
    color: #ffffff;
    font-size: 22px;
    font-weight: bold;
}

.property-requirement-steps__card__caption {
    grid-area: caption;
    color: #ffffff;
    font-size: 16px;
    font-weight: 300;
}

.property-requirement-steps__card__link {
    grid-area: link;
    color: #ffffff;
    font-size: 12px;
    font-weight: bold;
}

.property-requirement-steps__card:hover .property-requirement-steps__card__link {
    text-decoration: underline;
}

.property-requirement-steps__card__link .el-link--inner {
    display: grid;
    grid-auto-flow: column;
}

.property-requirement-steps__card__link__icon {
    justify-self: self-end;
}

.property-requirement-steps__header__name {
    font-weight: bold;
}

.property-requirement-steps__cards .el-row {
    white-space: nowrap;
    margin-top: 10px;
}

.property-requirement-steps__col {
    width: 350px;
    display: inline-block;
    float: none;
    margin: 20px 10px 25px 0;
}

.property-requirement-steps__col:first-of-type {
    margin-left: 40px;
}

.property-requirement-steps__col:last-of-type {
    margin-right: 40px;
}

.property-requirement-steps .el-link--inner:hover,
.property-requirement-steps .el-link:hover,
.property-requirement-steps .el-link.el-link--default:hover,
.property-requirement-steps .el-link.el-link--default {
  color: #ffffff;
  background-color: transparent;
}

.property-requirement-steps .el-link.is-underline:hover:after {
    content: none;
}
</style>
